import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { AuditorDateRangesPreferences } from '../../../data-models/preferences-v3.data-model';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../DataDisplay/useDisplayConfigState';
import { auditorDateRangesConfigSchema, getDefaultAuditorConfig } from './auditorDateRangesConfig.schema';
import { useAuditorDateRangesConfigActions } from './useAuditorDateRangesConfigActions';

export function useAuditorDateRangesConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const { config, saveConfig, resetConfig } = useAuditorDateRangesConfigActions();
  const schema = auditorDateRangesConfigSchema();
  const methods = useForm<AuditorDateRangesPreferences>({
    resolver: yupResolver(schema as ObjectSchema<AuditorDateRangesPreferences>),
    defaultValues: {
      ...getDefaultAuditorConfig(),
      ...config,
    },
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (isDirty && editStatus === 'initial') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Auditor Date Ranges Config', errors);
      return;
    }
    await saveConfig(getValues());
    setEditStatus('saved');
  }, [trigger, saveConfig, getValues, errors]);

  const onReset = useCallback(async () => {
    await resetConfig();
    reset(getDefaultAuditorConfig());
    setEditStatus('initial');
  }, [reset, resetConfig]);

  return { editStatus, onSave, onReset, methods };
}
